import { Card, Col, Row, Statistic, theme } from 'antd';
import type { ReactNode } from 'react';
import type { IncomingSummaryMetal } from '../../domain/metals/incomingSummary';
import {
    formatStockNumber,
    GRAMS_DIGITS,
    MONEY_DIGITS,
    type StockFigures,
} from '../../domain/metals/stockSummary';

interface FigureTile {
    key: string;
    labelKey: string;
    value: number | null;
    digits: number;
    unitKey: string;
}

const UNIT_GRAMS = 'metals.stockSummary.unit.grams';
const UNIT_RON = 'metals.stockSummary.unit.ron';
const UNIT_EUR = 'metals.stockSummary.unit.eur';
const UNIT_RON_PER_GRAM = 'metals.stockSummary.unit.ronPerGram';

const gramsTile = (key: string, labelKey: string, value: number | null): FigureTile => ({
    key,
    labelKey,
    value,
    digits: GRAMS_DIGITS,
    unitKey: UNIT_GRAMS,
});

const moneyTile = (
    key: string,
    labelKey: string,
    value: number | null,
    unitKey: string,
): FigureTile => ({
    key,
    labelKey,
    value,
    digits: MONEY_DIGITS,
    unitKey,
});

/** The weight tiles: gold in AU585 and AU999.99, any other metal in grams and fine grams. */
const weightTiles = (figures: StockFigures, kind: IncomingSummaryMetal): FigureTile[] =>
    figures.au585Quantity === null
        ? [
              gramsTile('quantity', 'metals.stockSummary.weight', figures.quantity),
              gramsTile('fine', `metals.stockSummary.fine.${kind}`, figures.purifiedQuantity),
          ]
        : [
              gramsTile('au585', 'metals.stockSummary.au585', figures.au585Quantity),
              gramsTile('au999', 'metals.stockSummary.au999', figures.purifiedQuantity),
          ];

const figureTiles = (
    figures: StockFigures,
    kind: IncomingSummaryMetal,
    pmpLabelKey: string,
): FigureTile[] => [
    ...weightTiles(figures, kind),
    { ...gramsTile('pmp', pmpLabelKey, figures.avgPrice), unitKey: UNIT_RON_PER_GRAM },
    moneyTile('ron', 'metals.stockSummary.worthRon', figures.valueRon, UNIT_RON),
    moneyTile('eur', 'metals.stockSummary.worthEur', figures.valueEur, UNIT_EUR),
];

export interface StockFiguresCardProps {
    t: (key: string) => string;
    testId: string;
    title: ReactNode;
    figures: StockFigures;
    kind: IncomingSummaryMetal;
    pmpLabelKey?: string;
}

/** The stock figures of one metal, or of the whole stock, as a row of statistics under a title. */
const StockFiguresCard = ({
    t,
    testId,
    title,
    figures,
    kind,
    pmpLabelKey = 'metals.pmp',
}: StockFiguresCardProps) => {
    const { token } = theme.useToken();

    return (
        <Card
            size="small"
            title={title}
            data-testid={testId}
            style={{ marginBottom: 8, background: token.colorFillAlter }}
            styles={{
                header: { minHeight: 28, padding: '0 12px', fontSize: 13 },
                body: { padding: '6px 12px' },
            }}
        >
            <Row gutter={[12, 4]}>
                {figureTiles(figures, kind, pmpLabelKey).map(figure => (
                    <Col key={figure.key} flex="1 1 130px" data-testid={`${testId}-${figure.key}`}>
                        <Statistic
                            title={<span style={{ fontSize: 11 }}>{t(figure.labelKey)}</span>}
                            suffix={<span style={{ fontSize: 11 }}>{t(figure.unitKey)}</span>}
                            value={
                                figure.value === null
                                    ? '–'
                                    : formatStockNumber(figure.value, figure.digits)
                            }
                            valueStyle={{ fontSize: 14, lineHeight: 1.3 }}
                        />
                    </Col>
                ))}
            </Row>
        </Card>
    );
};

export default StockFiguresCard;
